import { useEffect, useRef } from 'react';

/**
 * The sliver of Bootstrap 3.3.4's `dropdown.js` that the Backbone pages depend
 * on and that `react.html` does not ship.
 *
 * ★ PROMOTED OUT OF `pages/partners/` IN PHASE 7 · T1 (2026-08-05) — it is not a
 * partners concern. `data-toggle="dropdown"` appears in **20 CMS Admin templates
 * across 8 modules** (`components` 6, `activities` 4, `cost_codes` 3, `settings`
 * 2, `numbering` 2, `manufacturer`, `freight`, `componentTable`), i.e. in T1, T3,
 * T4, T6 and T9. `freight` — the FIRST module ported against the shell — needed
 * it for its row kebab, which is the only route to Delete on that screen.
 * Leaving it under `pages/partners/` would have meant every CMS tranche either
 * importing across a page boundary or growing its own copy. T1 exists to catch
 * exactly this, so the shell absorbed it. **Behaviour is unchanged**: a file
 * move plus three import rewrites, no edit to the logic below.
 *
 * ★ WHY IT EXISTS AT ALL. Legacy `/partners/` loads
 * `static/vendor/bootstrap-3.3.4-dist/js/bootstrap.js`, so both kebab menus
 * carry a live `data-toggle="dropdown"` binding. The partners view's own JS
 * only ever *hover-opens* the COMPANY kebab (`partners.js:71-85` adds `.open`
 * on a 150 ms timer) — everything else about both menus is Bootstrap:
 *
 *   - `Dropdown.prototype.toggle` (bootstrap.js:763-794) — a CLICK on the kebab
 *     calls `clearMenus()` first and then re-opens only if the menu was closed.
 *     So clicking a menu that hover already opened SHUTS it, and a second click
 *     re-opens it. Wave A had no click handler at all, so the click was inert.
 *   - `clearMenus()` (bootstrap.js:828-845), bound to `document`
 *     (bootstrap.js:893) — ANY click anywhere closes EVERY open dropdown. The
 *     partners view has no equivalent, so without this a menu opened by click
 *     would never close.
 *   - `getParent($this)` (bootstrap.js:847-857): the kebab buttons have neither
 *     `data-target` nor an `href`, so the parent is `$this.parent()` — the bare
 *     `<div class="">` between `.dropdown_company`/`.dropdown_contact` and the
 *     button. That is the SAME node `partners.js:75` adds `.open` to, which is
 *     why the two mechanisms compose instead of fighting.
 *   - `return false` at bootstrap.js:793 = `preventDefault()` +
 *     `stopPropagation()`, which is what stops the document-level `clearMenus`
 *     from immediately re-closing what the toggle just opened.
 *
 * ★ NOT PORTED (deliberately): the `ontouchstart` `.dropdown-backdrop` branch
 * (bootstrap.js:774-777, desktop-only app), the `show/shown/hide/hidden
 * .bs.dropdown` events (nothing on this page listens), `Dropdown.prototype
 * .keydown` (arrow-key menu navigation — the menus hold one or two items and no
 * legacy code depends on it), and `aria-expanded` on the kebab BUTTON
 * (bootstrap.js:786, :842). The last one is a real, if invisible, DOM
 * difference — see this wave's DEVIATIONS note.
 *
 * ★ THE `.open` CLASS IS NOT WHAT MAKES THE MENU VISIBLE ON ITS OWN. Both
 * `.dropdown_company` and `.dropdown_contact` are `display:none` until their
 * row is hovered (`partners.scss:98-101`, `:369`/`:392`), and Bootstrap's
 * `.open > .dropdown-menu { display:block }` does the rest. Off-hover, an
 * `.open` menu is still invisible — which is why legacy's uncancelled 150 ms
 * timer is harmless.
 */

/**
 * Every currently-open dropdown's "close me" callback. Module-level because
 * `clearMenus()` is GLOBAL in Bootstrap — clicking the company kebab closes an
 * open contact menu in another component, and a per-component `document`
 * listener could not do that once the toggle calls `stopPropagation()`.
 */
const openDropdowns = new Set<() => void>();

/** `clearMenus()` — bootstrap.js:828-845. Closes every open dropdown. */
export const clearMenus = (): void => {
  // Copy first: each callback removes itself from the set as it closes.
  Array.from(openDropdowns).forEach((close) => close());
};

/**
 * Registers one dropdown against both of Bootstrap's close triggers — the
 * document-level `click.bs.dropdown.data-api` handler (bootstrap.js:893) and
 * the global `clearMenus()` above.
 *
 * Only subscribes while the menu is OPEN, so the page carries one listener at a
 * time instead of one per row. The subscription lands in a passive effect,
 * i.e. after the opening click has finished propagating, so the click that
 * opens a menu can never be the click that closes it — the same net result as
 * Bootstrap's `return false`.
 */
export const useDropdownAutoClose = (isOpen: boolean, close: () => void): void => {
  const closeRef = useRef(close);
  closeRef.current = close;

  useEffect(() => {
    if (!isOpen) return undefined;
    const handler = () => closeRef.current();
    openDropdowns.add(handler);
    document.addEventListener('click', handler);
    return () => {
      openDropdowns.delete(handler);
      document.removeEventListener('click', handler);
    };
  }, [isOpen]);
};
